<template>
  <div class="tab-container">
    <!-- Default Tabs -->
    <section v-if="variant === 'default' || variant === 'all'">
      <div
        class="text-sm font-medium text-center text-gray-500 border-b border-gray-200 dark:text-gray-400 dark:border-gray-700"
      >
        <ul class="flex flex-wrap -mb-px">
          <li v-for="(tab, index) in tabs" :key="index" class="me-2">
            <a
              :href="tab.href"
              :class="{
                'inline-block p-4 text-blue-600 border-b-2 border-blue-600 rounded-t-lg active dark:text-blue-500 dark:border-blue-500':
                  tab.active,
                'inline-block p-4 border-b-2 border-transparent rounded-t-lg hover:text-gray-600 hover:border-gray-300 dark:hover:text-gray-300':
                  !tab.active && !tab.disabled,
                'inline-block p-4 text-gray-400 rounded-t-lg cursor-not-allowed dark:text-gray-500':
                  tab.disabled
              }"
              :aria-current="tab.active ? 'page' : null"
              >{{ tab.label }}</a
            >
          </li>
        </ul>
      </div>
    </section>

    <!-- Underline Tabs -->
    <section v-if="variant === 'underline' || variant === 'all'">
      <div class="border-b border-gray-200 dark:border-gray-700">
        <ul
          class="flex flex-wrap -mb-px text-sm font-medium text-center text-gray-500 dark:text-gray-400"
        >
          <li v-for="(tab, index) in tabs" :key="index" class="me-2">
            <a
              :href="tab.href"
              :class="{
                'inline-block p-4 text-blue-600 border-b-2 border-blue-600 rounded-t-lg active dark:text-blue-500 dark:border-blue-500':
                  tab.active,
                'inline-block p-4 border-b-2 border-transparent rounded-t-lg hover:text-gray-600 hover:border-gray-300 dark:hover:text-gray-300':
                  !tab.active && !tab.disabled,
                'inline-block p-4 text-gray-400 rounded-t-lg cursor-not-allowed dark:text-gray-500':
                  tab.disabled
              }"
              :aria-current="tab.active ? 'page' : null"
              >{{ tab.label }}</a
            >
          </li>
        </ul>
      </div>
    </section>

    <!-- Tabs with Icons -->
    <section v-if="variant === 'icons' || variant === 'all'">
      <div class="border-b border-gray-200 dark:border-gray-700">
        <ul
          class="flex flex-wrap -mb-px text-sm font-medium text-center text-gray-500 dark:text-gray-400"
        >
          <li v-for="(tab, index) in tabs" :key="index" class="me-2">
            <a
              :href="tab.href"
              :class="{
                'inline-flex items-center justify-center p-4 text-blue-600 border-b-2 border-blue-600 rounded-t-lg active dark:text-blue-500 dark:border-blue-500 group':
                  tab.active,
                'inline-flex items-center justify-center p-4 border-b-2 border-transparent rounded-t-lg hover:text-gray-600 hover:border-gray-300 dark:hover:text-gray-300 group':
                  !tab.active && !tab.disabled,
                'inline-flex items-center justify-center p-4 text-gray-400 rounded-t-lg cursor-not-allowed dark:text-gray-500':
                  tab.disabled
              }"
              :aria-current="tab.active ? 'page' : null"
            >
              <slot :name="`icon-${index}`">
                <svg
                  class="w-4 h-4 me-2"
                  :class="{
                    'text-blue-600 dark:text-blue-500': tab.active,
                    'text-gray-400 group-hover:text-gray-500 dark:text-gray-500 dark:group-hover:text-gray-300':
                      !tab.active && !tab.disabled,
                    'text-gray-400 dark:text-gray-500': tab.disabled
                  }"
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path :d="tab.icon" />
                </svg>
              </slot>
              {{ tab.label }}
            </a>
          </li>
        </ul>
      </div>
    </section>

    <!-- Pills -->
    <section v-if="variant === 'pills' || variant === 'all'">
      <ul
        class="flex flex-wrap text-sm font-medium text-center text-gray-500 dark:text-gray-400"
      >
        <li v-for="(tab, index) in tabs" :key="index" class="me-2">
          <a
            :href="tab.href"
            :class="{
              'inline-block px-4 py-3 text-white bg-blue-600 rounded-lg active':
                tab.active,
              'inline-block px-4 py-3 rounded-lg hover:text-gray-900 hover:bg-gray-100 dark:hover:bg-gray-800 dark:hover:text-white':
                !tab.active && !tab.disabled,
              'inline-block px-4 py-3 text-gray-400 cursor-not-allowed dark:text-gray-500':
                tab.disabled
            }"
            :aria-current="tab.active ? 'page' : null"
            >{{ tab.label }}</a
          >
        </li>
      </ul>
    </section>

    <!-- Vertical Tabs -->
    <section v-if="variant === 'vertical' || variant === 'all'">
      <div class="md:flex">
        <ul
          class="flex-column space-y-4 text-sm font-medium text-gray-500 dark:text-gray-400 md:me-4 mb-4 md:mb-0"
        >
          <li v-for="(tab, index) in tabs" :key="index">
            <a
              :href="tab.href"
              :class="{
                'inline-flex items-center px-4 py-3 text-white bg-blue-700 rounded-lg active w-full dark:bg-blue-600':
                  tab.active,
                'inline-flex items-center px-4 py-3 rounded-lg hover:text-gray-900 bg-gray-50 hover:bg-gray-100 w-full dark:bg-gray-800 dark:hover:bg-gray-700 dark:hover:text-white':
                  !tab.active && !tab.disabled,
                'inline-flex items-center px-4 py-3 text-gray-400 rounded-lg cursor-not-allowed bg-gray-50 w-full dark:bg-gray-800 dark:text-gray-500':
                  tab.disabled
              }"
              :aria-current="tab.active ? 'page' : null"
            >
              <slot :name="`icon-${index}`">
                <svg
                  class="w-4 h-4 me-2"
                  :class="{
                    'text-white': tab.active,
                    'text-gray-500 dark:text-gray-400':
                      !tab.active && !tab.disabled,
                    'text-gray-400 dark:text-gray-500': tab.disabled
                  }"
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path :d="tab.icon" />
                </svg>
              </slot>
              {{ tab.label }}
            </a>
          </li>
        </ul>
      </div>
    </section>

    <!-- Full Width Tabs -->
    <section v-if="variant === 'fullWidth' || variant === 'all'">
      <div
        class="text-sm font-medium text-center text-gray-500 border-b border-gray-200 dark:text-gray-400 dark:border-gray-700"
      >
        <ul class="flex flex-wrap -mb-px">
          <li v-for="(tab, index) in tabs" :key="index" class="flex-1">
            <a
              :href="tab.href"
              :class="{
                'inline-block w-full p-4 text-blue-600 border-b-2 border-blue-600 rounded-t-lg active dark:text-blue-500 dark:border-blue-500':
                  tab.active,
                'inline-block w-full p-4 border-b-2 border-transparent rounded-t-lg hover:text-gray-600 hover:border-gray-300 dark:hover:text-gray-300':
                  !tab.active && !tab.disabled,
                'inline-block w-full p-4 text-gray-400 rounded-t-lg cursor-not-allowed dark:text-gray-500':
                  tab.disabled
              }"
              :aria-current="tab.active ? 'page' : null"
              >{{ tab.label }}</a
            >
          </li>
        </ul>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";

interface Tab {
  label: string;
  href?: string;
  active?: boolean;
  disabled?: boolean;
  icon?: string;
}

const props = defineProps<{
  tabs: Tab[];
  variant:
    | "default"
    | "underline"
    | "icons"
    | "pills"
    | "vertical"
    | "fullWidth"
    | "all";
}>();

const activeTab = computed(
  () => props.tabs.find((tab) => tab.active) || props.tabs[0]
);
</script>

<style scoped>
/* Add any component-specific styles here */
</style>
